// 三方渠道价格的纯计算：折扣、优惠金额、折扣描述与价格合法性
// 口径来自需求：折扣 = (官方价 - 渠道价) / 官方价 × 100，四舍五入取整；优惠金额保留两位小数

// 三方价格下限：不得低于苹果官方价的 7 折；批量开启的价格比例同口径（≥70%）
export const MIN_PRICE_RATIO_PERCENT = 70
const MIN_PRICE_RATIO = MIN_PRICE_RATIO_PERCENT / 100

// 官方价缺失时的默认提示；分层商品由调用方替换成「请先填写 T1 默认价」
export const DEFAULT_MISSING_OFFICIAL_TIP = '请先在苹果官方价格中设置默认价格'

const toAmount = (value?: string | number | null): number | null => {
  const num = parseFloat(String(value ?? ''))
  return Number.isFinite(num) ? num : null
}

// 折扣百分比，只读展示用；官方价或渠道价缺失时返回 null（输入框留空）
export const calcDiscountRate = (
  officialPrice?: string | number | null,
  channelPrice?: string | number | null,
): number | null => {
  const official = toAmount(officialPrice)
  const channel = toAmount(channelPrice)
  if (official === null || channel === null || official <= 0 || channel <= 0) return null
  // 先换算成整数「分」再先乘后除，避免浮点误差把 28.5 这类临界值算成 28.499…（与后端定点运算对齐）
  const officialCents = Math.round(official * 100)
  const channelCents = Math.round(channel * 100)
  return Math.round(((officialCents - channelCents) * 100) / officialCents)
}

// 优惠金额，保留两位小数
export const calcSavedAmount = (
  officialPrice?: string | number | null,
  channelPrice?: string | number | null,
): string | null => {
  const official = toAmount(officialPrice)
  const channel = toAmount(channelPrice)
  if (official === null || channel === null || official <= 0 || channel <= 0) return null
  return (official - channel).toFixed(2)
}

// 折扣描述；渠道价等于官方价（无优惠）时留空
export const buildPromotionDesc = (
  officialPrice?: string | number | null,
  channelPrice?: string | number | null,
): string => {
  const rate = calcDiscountRate(officialPrice, channelPrice)
  const saved = calcSavedAmount(officialPrice, channelPrice)
  if (rate === null || saved === null || rate <= 0) return ''
  return `You will save $${saved} on this purchase, a discount of about ${rate}%.`
}

// 官方价 × 7 折的价格下限，保留两位小数（与价格输入精度一致）
export const calcMinChannelPrice = (officialPrice?: string | number | null): number | null => {
  const official = toAmount(officialPrice)
  if (official === null || official <= 0) return null
  return Math.round(official * MIN_PRICE_RATIO * 100) / 100
}

// 校验渠道价格，返回错误文案，空串表示通过
// 合法区间：[官方价 × 70%, 官方价]；1.99 美元只在提示文案里建议，不做拦截
export const validateChannelPrice = (
  officialPrice?: string | number | null,
  channelPrice?: string | number | null,
  missingOfficialTip: string = DEFAULT_MISSING_OFFICIAL_TIP,
): string => {
  const channel = toAmount(channelPrice)
  if (channel === null) return '请输入默认价格'
  if (channel <= 0) return '默认价格必须大于 0'
  const official = toAmount(officialPrice)
  if (official === null || official <= 0) return missingOfficialTip
  if (channel > official) return '默认价格不得高于苹果官方价格'
  const min = calcMinChannelPrice(official) as number
  if (channel < min) return `默认价格不得低于苹果官方价格的 7 折（${min.toFixed(2)} USD），请重新输入`
  return ''
}
